import { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Separator } from "@/components/ui/separator";
import { Phone, Envelope, MapPin, List, X, CaretRight } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const productLinks = [
  { to: "/products/bitumen-solutions", label: "Bitumen Solutions", desc: "Emulsions, primes & penetration grades" },
  { to: "/products/bitumen-equipment", label: "Bitumen Equipment", desc: "SprayMaster equipment range" },
  { to: "/products/specialty-equipment", label: "Specialty Equipment", desc: "Cold mix plants & debagging units" },
  { to: "/products/heavy-machinery", label: "Heavy Machinery", desc: "FTM crushing & screening equipment" },
];

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setMobileOpen(false); }, [location]);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "glass-header" : "bg-white shadow-sm"}`} data-testid="main-header">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link to="/" className="flex items-center group" data-testid="logo-link">
            <img src="/images/logo/kznsc-logo.jpeg" alt="KwaZulu Supplies & Construction" className="h-16 lg:h-20 w-auto object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            <NavLink to="/" label="Home" />
            <NavLink to="/about" label="About Us" />
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent hover:bg-[#E0F5F2] text-[#0B2D2D] font-body font-semibold text-sm rounded-sm h-10 px-4 data-[state=open]:bg-[#E0F5F2]">
                    Products
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[400px] bg-white border border-gray-100 shadow-xl p-4 rounded-sm" data-testid="products-dropdown">
                      <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#00A091] mb-3 px-3">Product Range</p>
                      <div className="grid gap-0.5">
                        {productLinks.map((item) => (
                          <NavigationMenuLink key={item.to} asChild>
                            <Link to={item.to} className="flex items-center gap-3 p-3 rounded-sm hover:bg-[#E0F5F2] transition-all group" data-testid={`product-link-${item.to.split('/').pop()}`}>
                              <CaretRight size={14} weight="bold" className="text-[#00A091] opacity-0 group-hover:opacity-100 transition-opacity" />
                              <div>
                                <span className="font-heading font-bold text-[#0B2D2D] text-sm block group-hover:text-[#00A091] transition-colors">{item.label}</span>
                                <span className="font-body text-xs text-[#0B2D2D]/50 block">{item.desc}</span>
                              </div>
                            </Link>
                          </NavigationMenuLink>
                        ))}
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            <NavLink to="/steel-fabrication" label="Steel Fabrication" />
            <NavLink to="/project-portfolio" label="Portfolio" />
            <NavLink to="/contact" label="Contact" />
          </nav>

          <div className="flex items-center gap-4">
            <Link to="/contact" className="hidden md:inline-flex items-center gap-2 bg-[#00A091] text-white font-bold uppercase tracking-wider text-xs px-6 py-3 rounded-sm hover:bg-[#0B2D2D] transition-all duration-300" data-testid="header-cta">
              Get A Quote
            </Link>
            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 text-[#0B2D2D]" data-testid="mobile-menu-toggle">
              {mobileOpen ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden bg-white border-t border-gray-100 overflow-hidden shadow-lg" data-testid="mobile-menu">
            <div className="px-6 py-4 space-y-1">
              <MobileLink to="/" label="Home" />
              <MobileLink to="/about" label="About Us" />
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#00A091] pt-3 pb-1">Products</p>
              {productLinks.map((item) => (<MobileLink key={item.to} to={item.to} label={item.label} indent />))}
              <MobileLink to="/steel-fabrication" label="Steel Fabrication" />
              <MobileLink to="/project-portfolio" label="Portfolio" />
              <MobileLink to="/contact" label="Contact" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function NavLink({ to, label }) {
  const location = useLocation();
  const active = location.pathname === to;
  return (
    <Link to={to} className={`relative font-body font-semibold text-sm px-4 py-2 transition-colors line-expand ${active ? "text-[#00A091]" : "text-[#0B2D2D] hover:text-[#00A091]"}`}>{label}</Link>
  );
}

function MobileLink({ to, label, indent }) {
  const location = useLocation();
  const active = location.pathname === to;
  return (
    <Link to={to} className={`block py-2.5 font-body font-semibold text-sm transition-colors ${indent ? "pl-4" : ""} ${active ? "text-[#00A091]" : "text-[#0B2D2D] hover:text-[#00A091]"}`}>{label}</Link>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0B2D2D] text-[#E0F5F2] relative" data-testid="main-footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="mb-6">
              <img src="/images/logo/kznsc-logo-transparent.png" alt="KwaZulu Supplies & Construction" className="h-14 w-auto object-contain" />
            </div>
            <p className="text-[#E0F5F2]/60 text-sm leading-relaxed max-w-xs">Building the Backbone of Southern African Infrastructure. Premier supplier of bitumen products, heavy machinery, and steel fabrication services.</p>
          </div>
          <div className="md:col-span-2">
            <h4 className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#A7D129] mb-4">Navigate</h4>
            <div className="space-y-2">
              {[{to:"/",label:"Home"},{to:"/about",label:"About Us"},{to:"/steel-fabrication",label:"Steel Fabrication"},{to:"/project-portfolio",label:"Portfolio"},{to:"/contact",label:"Contact"}].map(l=>(
                <Link key={l.to} to={l.to} className="block text-sm text-[#E0F5F2]/60 hover:text-[#A7D129] transition-colors">{l.label}</Link>
              ))}
            </div>
          </div>
          <div className="md:col-span-3">
            <h4 className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#A7D129] mb-4">Products</h4>
            <div className="space-y-2">
              {productLinks.map(l=>(<Link key={l.to} to={l.to} className="block text-sm text-[#E0F5F2]/60 hover:text-[#A7D129] transition-colors">{l.label}</Link>))}
            </div>
          </div>
          <div className="md:col-span-3">
            <h4 className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#A7D129] mb-4">Contact</h4>
            <div className="space-y-3">
              <a href="tel:+27748360898" className="flex items-center gap-3 text-sm text-[#E0F5F2]/60 hover:text-[#A7D129] transition-colors"><Phone size={16} weight="duotone" className="text-[#4EC9BE]" />+27 74 836 0898</a>
              <a href="mailto:info@kznsc.co.za" className="flex items-center gap-3 text-sm text-[#E0F5F2]/60 hover:text-[#A7D129] transition-colors"><Envelope size={16} weight="duotone" className="text-[#4EC9BE]" />info@kznsc.co.za</a>
              <div className="flex items-start gap-3 text-sm text-[#E0F5F2]/60"><MapPin size={16} weight="duotone" className="text-[#4EC9BE] mt-0.5 flex-shrink-0" /><span>Unit 14 Halstead Park, 15 Halstead Road, Mkodeni, Pietermaritzburg, 3201</span></div>
            </div>
          </div>
        </div>
        <Separator className="bg-[#E0F5F2]/10 my-10" />
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-[#E0F5F2]/40">&copy; {new Date().getFullYear()} KwaZulu Supplies &amp; Construction (Pty) Ltd. Reg: 2011/076665 | VAT: 4940263272</p>
          <a href="https://www.kznsc.co.za" target="_blank" rel="noopener noreferrer" className="text-xs text-[#4EC9BE] hover:text-[#A7D129] transition-colors">www.kznsc.co.za</a>
        </div>
      </div>
    </footer>
  );
}

function PrivacyNotice() {
  const [visible, setVisible] = useState(() => !localStorage.getItem("kznsc_privacy_accepted"));
  if (!visible) return null;
  const handle = (accepted) => {
    if (accepted) localStorage.setItem("kznsc_privacy_accepted", "true");
    setVisible(false);
  };
  return (
    <AnimatePresence>
      <motion.div initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -40, opacity: 0 }} className="fixed top-0 left-0 right-0 z-[60] bg-[#0B2D2D] text-white py-2.5 px-4" data-testid="privacy-notice">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-4 flex-wrap">
          <p className="text-xs text-[#E0F5F2]/80">This website uses cookies to ensure you get the best experience. By continuing to use this site, you agree to our privacy policy.</p>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => handle(true)} className="bg-[#A7D129] text-[#0B2D2D] font-bold text-xs px-4 py-1.5 rounded-sm hover:bg-[#00A091] hover:text-white transition-colors" data-testid="privacy-accept">I Accept</button>
            <button onClick={() => handle(false)} className="border border-white/30 text-white font-bold text-xs px-4 py-1.5 rounded-sm hover:bg-white/10 transition-colors" data-testid="privacy-decline">I Decline</button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function Layout() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <div className="min-h-screen flex flex-col">
      <PrivacyNotice />
      <Header />
      <main className="flex-1 pt-20 lg:pt-24"><Outlet /></main>
      <Footer />
      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/27748360898?text=Hi%20KZNSC%2C%20I%27d%20like%20to%20enquire%20about%20your%20products%20and%20services."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-20 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 hover:shadow-xl transition-all duration-300 group"
        data-testid="whatsapp-button"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="absolute right-full mr-3 bg-white text-[#0B2D2D] text-xs font-bold px-3 py-2 rounded-sm shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          Chat with us
        </span>
      </a>
    </div>
  );
}
